import React from 'react';
import { createUseStyles, useTheme, createGenerateId, JssProvider, ThemeProvider } from 'react-jss';
import { useSurveyTheme } from '@sm/webassets';
const smFont = "'National 2', 'Helvetica Neue', Helvetica, Arial, 'Hiragino Sans', 'Hiragino Kaku Gothic Pro', '游ゴシック', '游ゴシック体', YuGothic, 'Yu Gothic', 'ＭＳ ゴシック', 'MS Gothic', sans-serif";
const grey = 'rgba(0,0,0,0.05)';
/** SmQuestion specific augmentation to the SurveyTheme  */

const themeAugmentation = {
  // Tokens - shared across all surveys
  fontFamily: smFont,
  fontSize: {
    body: 16
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500
  },
  nextButton: {
    backgroundColor: '#00BF6F',
    color: '#FFFFFF'
  },
  input: {
    activeColor: '#00BF6F',
    // Light | Dark BG are shared across all surveys
    bgColor: grey
  },
  border: {
    defaultColor: '#999',
    correctColor: '#00BF6F',
    wrongColor: '#F05B24'
  },
  correctAnswerHighlightColor: grey
};
/**
 * Calculates highlight/selected color based on theme-properties
 * @see https://code.corp.surveymonkey.com/devmonkeys/smlib.surveytemplates/blob/6a4520867c756527c4ae4c0861b64e41ae3dd196/smlib/surveytemplates/themes_v3/base_css.jinja2#L249-L274
 * @param {SurveyThemeType} theme survey-theme object
 * @param {boolean} [isNps=true] use `nps` value (default true).
 * In the original, the current CSS always uses the nps value
 * @returns {string} the RGBA color value
 *
 * @todo move logic to @sm/webassets:SurveyTheme or direct theme-values
 */

const getQuestionHighlightColor = (theme, isNps = true) => {
  var _a;

  const titleColor = (_a = theme.titleColor) === null || _a === void 0 ? void 0 : _a.replace(/\s/g, ''); // theme with dark background

  if (theme.isDark) {
    return isNps ? 'rgba(0,0,0,0.40)' : 'rgba(0,0,0,0.15)';
  }
  /**
   * Iceberg and Arctic Specs
   * @see https://momentiveai.atlassian.net/browse/ENT-3452
   *
   * @todo change to use `theme.name` when added to @sm/webassets
   */


  if (titleColor === 'rgba(5,70,126,1)') {
    // Arctic theme rgba of #c9f2f4
    return 'rgba(201,242,244,1)';
  }

  if (titleColor === 'rgba(0,0,96,1)') {
    // Iceberg theme rgba of #1fd8ea
    return 'rgba(31,216,234,1)';
  }

  return 'rgba(0,0,0,0.05)'; // Pastel theme
};
/** Augments `theme` with SmQuestion specific theme attributes */


const applySurveyThemeAugmentation = theme => {
  var _a, _b, _c;

  const questionHighlightColor = getQuestionHighlightColor(theme);
  return { ...themeAugmentation,
    nextButton: {
      backgroundColor: theme.primaryAccentColor || themeAugmentation.nextButton.backgroundColor,
      color: theme.secondaryAccentColor || themeAugmentation.nextButton.color
    },
    input: {
      activeColor: theme.primaryAccentColor || themeAugmentation.input.activeColor,
      bgColor: questionHighlightColor
    },
    border: {
      defaultColor: theme.isAccessible ? (_a = theme.answerColor) !== null && _a !== void 0 ? _a : '#999' : '#999',
      correctColor: theme.isAccessible ? (_b = theme.answerColor) !== null && _b !== void 0 ? _b : '#999' : '#00BF6F',
      wrongColor: theme.isAccessible ? (_c = theme.answerColor) !== null && _c !== void 0 ? _c : '#999' : '#F05B24'
    },
    correctAnswerHighlightColor: questionHighlightColor,
    ...theme // keep theme last to allow user to overwrite augmentation

  };
};
/**
 * Dummy SurveyTheme
 *
 *  _Not a real theme - only use for testing_
 */


const DUMMY_SURVEY_THEME = {
  name: 'Dummy Survey Theme',
  // Survey-specific styles
  layout: 'BACKGROUND_FULL_TEXT_CENTER',
  titleColor: 'rgba(255,51,0,1)',
  questionColor: 'rgba(79,122,39,1)',
  answerColor: 'rgba(56,136,254,1)',
  primaryAccentColor: '#00BF6F',
  secondaryAccentColor: '#FFFFFF',
  primaryBackgroundColor: '#F7F8FA',
  secondaryBackgroundColor: '#ccf2e2',
  isAccessible: false,
  isDark: false,
  isCustomTheme: false,
  // Survey-specific component Styles
  surveyPage: {
    overlayColor: 'rgba(217,238,236,0.68)',
    backgroundImage: 'url(https://images.pexels.com/photos/2760856/pexels-photo-2760856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
  },
  surveyTitle: {
    fontSize: 25,
    fontFamily: 'Montserrat,Arial,Helvetica',
    fontWeight: 700,
    fontStyle: 'normal',
    textDecoration: 'none'
  },
  pageTitle: {
    fontSize: 25,
    fontFamily: 'Montserrat,Arial,Helvetica',
    fontWeight: 700,
    fontStyle: 'normal',
    textDecoration: 'none'
  },
  button: {
    fontSize: 15,
    fontFamily: 'National2',
    fontWeight: 400
  },
  error: {
    fontSize: 16,
    fontFamily: 'arial,sans-serif',
    fontWeight: 500,
    fontStyle: 'normal',
    textDecoration: 'none'
  },
  exitLink: {
    fontSize: 12,
    fontWeight: 300,
    backgroundColor: 'rgba(0,0,0,0)'
  },
  logo: {
    enabled: true,
    image: {
      url: 'https://sm-assets-mt1.s3.amazonaws.com/survey/518810755/6816175a-dfd7-4cc3-8b5c-64fd07d6f491.jpeg'
    }
  },
  progressBar: {
    enabled: true,
    position: 'DISABLED',
    showPercent: true,
    showPageNumber: true
  },
  questionBody: {
    color: '#333e48',
    highlightColor: 'rgba(208,210,211,1)',
    fontSize: 18,
    fontFamily: smFont,
    fontWeight: 300,
    fontStyle: 'normal',
    textDecoration: 'none'
  },
  questionTitle: {
    fontSize: 20,
    fontFamily: smFont,
    fontWeight: 300,
    fontStyle: 'normal',
    textDecoration: 'none',
    color: '#333e48'
  },
  breakpoints: {
    xxs: {
      max: '480px'
    },
    xs: {
      min: '481px',
      max: '640px'
    },
    sm: {
      min: '641px',
      max: '768px'
    },
    md: {
      min: '769px',
      max: '1024px'
    },
    lg: {
      min: '1025px',
      max: '1200px'
    },
    xl: {
      min: '1201px'
    }
  }
};
/**
 * Dummy AugmentedSurveyTheme (pre-augmented SurveyTheme)
 *
 *  _Not a real theme - only use for testing_
 */

const DUMMY_AUGMENTED_SURVEY_THEME = { ...DUMMY_SURVEY_THEME,
  ...themeAugmentation
};
const prefix = 'smqr-';
const selectors = [`[class*='${prefix}']`, `[class*='${prefix}']::before`, `[class*='${prefix}']::after`];
const resetStyles = {
  '-webkit-font-smoothing': 'antialiased',
  boxSizing: 'border-box',
  listStyle: 'none',
  margin: 0,
  padding: 0
};
const useStyles = createUseStyles({
  '@global': {
    [selectors.join(',')]: resetStyles
  }
}, {
  // Ensure this gets added to as the first stylesheet
  // ref: https://github.com/cssinjs/jss/blob/master/packages/react-jss/src/utils/getSheetIndex.js#L3-L16
  index: (Number.MIN_SAFE_INTEGER || -1e9) - 1,
  name: 'smquestions-respondent-global-styles'
});
/* Must by rendered inside a ThemeProvider with the RespondentTheme */

function SurveyGlobalStyles() {
  const theme = useTheme();
  useStyles({
    theme
  });
  return null;
}
/* Wraps all components and ThemeProvider of the Respondent experience */


function SurveyStylesProvider({
  children,
  ...props
}) {
  // https://cssinjs.org/jss-api?v=v10.4.0#generate-your-class-names
  const generateJssId = React.useMemo(() => createGenerateId(), []);

  const generateId = (rule, srcSheet) => {
    var _a;

    const sheet = srcSheet;

    if (sheet) {
      sheet.options.classNamePrefix = prefix;
      sheet.options.index = (_a = sheet.options.index) !== null && _a !== void 0 ? _a : 10;
    }

    return generateJssId(rule, sheet);
  };

  return React.createElement(JssProvider, { ...props,
    generateId: generateId
  }, children);
}

function SurveyThemeWrapper({
  children
}) {
  const theme = useSurveyTheme();
  return React.createElement(SurveyStylesProvider, null, React.createElement(ThemeProvider, {
    theme: applySurveyThemeAugmentation(theme)
  }, React.createElement(SurveyGlobalStyles, null), children));
}

export { DUMMY_AUGMENTED_SURVEY_THEME, DUMMY_SURVEY_THEME, SurveyGlobalStyles, SurveyStylesProvider, SurveyThemeWrapper, applySurveyThemeAugmentation };